import { useMemo } from 'react';

import { Badge } from '@common/ui/Badge';
import { Button } from '@common/ui/buttons/Button';
import { CachedImage } from '@common/ui/CachedImage';
import { Card, type CardProps } from '@common/ui/Card';

import styles from './TaskCard.module.scss';

export interface TaskCardProps extends CardProps {
  title: string;
  subtitle: string;
  premiumDays?: number; // legacy days-based reward
  premiumHours?: number; // preferred hours-based reward
  loading?: boolean;
  status?: 'completed' | 'available';
  buttonTitle: string;
}

export const TaskCard = ({
  title,
  subtitle,
  premiumDays,
  premiumHours,
  loading,
  buttonTitle = 'Подписаться',
  status = 'available',
  ...props
}: TaskCardProps) => {
  const currentPremiumTime = useMemo(() => {
    if (typeof premiumDays === 'number' && premiumDays > 0) {
      const isIntegerDays = Number.isInteger(premiumDays);

      return isIntegerDays
        ? `+${premiumDays} д`
        : `+${Math.round(premiumDays * 24)} ч`;
    }

    if (typeof premiumHours === 'number' && premiumHours > 0) {
      return `+${premiumHours} ч`;
    }
  }, [premiumDays, premiumHours]);

  return (
    <Card {...props} className={styles.card}>
      <div className={styles.header}>
        <span className={styles.title}>{title}</span>

        <Badge
          testId="task-card-badge"
          className={styles.badge}
          color="premium"
          prefix={
            <CachedImage
              testId="cached-image"
              alt="badge"
              className={styles.badgeIcon}
              src="/icons/3d-icons/fire.png"
              blur="2px"
            />
          }
          text={currentPremiumTime}
        />
      </div>

      <span className={styles.subtitle}>{subtitle}</span>

      {status === 'available' && (
        <Button
          testId="task-card-action-button"
          className={styles.button}
          loading={loading}
          variant="premiumGradient"
        >
          {buttonTitle}
        </Button>
      )}
    </Card>
  );
};
